import { useState, useRef, useCallback } from 'react'
import { Link, usePage, router } from '@inertiajs/react'
import { SharedProps } from '../../types'
import UserAvatar from '../shared/UserAvatar'
import { LABELS } from '../../pages/AccountabilityCharts/labels'

export default function GlobalNav({ variant = 'default' }: { variant?: 'default' | 'scorecard' }) {
  const { current_user, can_access_company_billing, can_access_guide_billing, user_teams, current_team, available_organizations, current_organization, published_charts, can_manage_charts, csrf_token, impersonation } = usePage().props as unknown as SharedProps
  const [teamsOpen, setTeamsOpen] = useState(false)
  const [orgSwitcherOpen, setOrgSwitcherOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const orgDropdownRef = useRef<HTMLDivElement>(null)
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const orgCloseTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cancelClose = useCallback(() => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current)
      closeTimeout.current = null
    }
  }, [])

  const scheduleClose = useCallback(() => {
    cancelClose()
    closeTimeout.current = setTimeout(() => {
      setTeamsOpen(false)
    }, 200)
  }, [cancelClose])

  const handleMouseEnter = useCallback(() => {
    cancelClose()
    setTeamsOpen(true)
  }, [cancelClose])

  const handleMouseLeave = useCallback(() => {
    scheduleClose()
  }, [scheduleClose])

  const cancelOrgClose = useCallback(() => {
    if (orgCloseTimeout.current) {
      clearTimeout(orgCloseTimeout.current)
      orgCloseTimeout.current = null
    }
  }, [])

  const scheduleOrgClose = useCallback(() => {
    cancelOrgClose()
    orgCloseTimeout.current = setTimeout(() => {
      setOrgSwitcherOpen(false)
    }, 200)
  }, [cancelOrgClose])

  const handleSwitchOrg = useCallback((orgId: number) => {
    setOrgSwitcherOpen(false)
    router.patch('/account/switch_organization', { organization_id: orgId })
  }, [])

  if (!current_user) {
    if (!can_access_guide_billing) return null
    return (
      <nav className="pinnacle-global-nav bg-pinnacle-red min-h-14 flex items-center flex-wrap gap-3 px-6 py-3 text-white">
        <Link href="/workspaces" className="flex items-center gap-2 mr-4">
          <img src="/pinnacle-logo.png" alt="Pinnacle" className="w-7 h-7 rounded-full object-cover" />
          <img src="/images/pinnacle-wordmark-white.png" alt="Pinnacle Business Guides" className="h-6 w-auto object-contain" />
        </Link>
        <Link href="/workspaces" className="font-sans font-medium px-3 py-2">Workspaces</Link>
        <Link href="/guide_billing" className="font-sans font-medium px-3 py-2">Guide Billing</Link>
        <Link href="/guide_tenants/new" className="font-sans font-medium px-3 py-2">Create Company</Link>
        {!impersonation && <button type="button" onClick={() => router.delete('/users/sign_out', { data: { authenticity_token: csrf_token ?? '' } })} className="ml-auto font-sans text-sm font-medium">Sign Out</button>}
      </nav>
    )
  }

  const isAdmin = current_user.role === 'admin' || current_user.role === 'account_owner'
  const isGuide = current_user.role === 'guide'
  const canSeeCompanyBilling = can_access_company_billing === true
  const isTempAdmin = !!current_user.is_temporary_admin
  // Company support mode is locked to one company, but Guide impersonation has
  // to keep the Guide's own company dropdown usable.
  const canSwitchOrganizations = !impersonation || impersonation.mode === 'guide'
  const showOrgSwitcher = canSwitchOrganizations && available_organizations && available_organizations.length > 1

  return (
    <nav className={`pinnacle-global-nav ${variant === 'scorecard' ? 'pinnacle-global-nav--scorecard' : 'bg-pinnacle-red h-14'} flex items-center px-6 text-white`}>
      <div className="flex items-center gap-1 flex-1">
        <Link href="/" className="flex items-center gap-2 mr-4">
          <img src="/pinnacle-logo.png" alt="Pinnacle" className="w-7 h-7 rounded-full object-cover" />
          <img src="/images/pinnacle-wordmark-white.png" alt="Pinnacle Business Guides" className="h-6 w-auto object-contain" />
        </Link>

        {showOrgSwitcher && (
          <div
            ref={orgDropdownRef}
            className="relative mr-2"
            onMouseEnter={() => { cancelOrgClose(); setOrgSwitcherOpen(true) }}
            onMouseLeave={scheduleOrgClose}
          >
            <button
              onClick={() => setOrgSwitcherOpen(!orgSwitcherOpen)}
              className="flex items-center gap-1.5 font-sans text-sm font-medium text-white/90 hover:text-white transition-colors px-2.5 py-1.5 rounded-md bg-white/10 hover:bg-white/15"
            >
              <span className="max-w-[160px] truncate">{current_organization?.name}</span>
              <svg className={`w-3.5 h-3.5 transition-transform ${orgSwitcherOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {orgSwitcherOpen && (
              <div
                className="absolute top-full left-0 pt-1 z-50"
                onMouseEnter={cancelOrgClose}
                onMouseLeave={scheduleOrgClose}
              >
                <div className="bg-white rounded-lg shadow-elevated border border-stone/20 py-1 min-w-[220px]">
                  <div className="px-3 py-1.5 text-xs font-semibold text-text-muted uppercase tracking-wider">
                    Organizations
                  </div>
                  {available_organizations.map(org => (
                    <button
                      key={org.id}
                      onClick={() => handleSwitchOrg(org.id)}
                      disabled={org.active}
                      className={`block w-full text-left px-4 py-2 text-base transition-colors ${
                        org.active
                          ? 'bg-charcoal/10 text-text-primary font-medium cursor-default'
                          : 'text-text-primary hover:bg-stone/30 cursor-pointer'
                      }`}
                    >
                      <span>{org.name}</span>
                      {org.active && (
                        <svg className="w-4 h-4 inline ml-2 text-text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <Link href="/" className="font-sans text-base font-medium text-text-on-dark hover:text-white transition-colors px-3 py-2">
          Home
        </Link>
        {isGuide && (
          <Link href="/guide_dashboard" className="font-sans text-base font-medium text-text-on-dark hover:text-white transition-colors px-3 py-2">
            All Accounts
          </Link>
        )}
        {isAdmin && (
          <Link href="/meetings" className="font-sans text-base font-medium text-text-on-dark hover:text-white transition-colors px-3 py-2">
            Meeting History
          </Link>
        )}
        {canSeeCompanyBilling && (
          <Link href="/admin/billing" className="font-sans text-base font-medium text-text-on-dark hover:text-white transition-colors px-3 py-2">
            Billing
          </Link>
        )}
        {can_access_guide_billing && (
          <Link href="/guide_billing" className="font-sans text-base font-medium text-text-on-dark hover:text-white transition-colors px-3 py-2">
            Guide Billing
          </Link>
        )}
        {can_access_guide_billing && (
          <Link href="/guide_tenants/new" className="font-sans text-base font-medium text-text-on-dark hover:text-white transition-colors px-3 py-2">
            Create Company
          </Link>
        )}
        {isTempAdmin && (
          <Link href="/company/handoff" className="font-sans text-base font-medium text-text-on-dark hover:text-white transition-colors px-3 py-2">
            Hand Off
          </Link>
        )}
        {/* Inbox and Reports are early/unbuilt features — hidden from nav for now (routes/pages remain).
        <Link href="/inbox" className="font-sans text-base font-medium text-text-on-dark hover:text-white transition-colors px-3 py-2 relative">
          Inbox
        </Link>
        <Link href="/reports" className="font-sans text-base font-medium text-text-on-dark hover:text-white transition-colors px-3 py-2">
          Reports
        </Link>
        */}

        <span className="text-text-on-dark mx-1 select-none">|</span>

        <div
          ref={dropdownRef}
          className="relative"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="flex items-center">
            <Link
              href="/teams"
              className="font-sans text-base font-medium text-text-on-dark hover:text-white transition-colors pl-3 pr-1 py-2"
            >
              Teams
            </Link>
            <button
              onClick={(e) => {
                e.stopPropagation()
                setTeamsOpen(!teamsOpen)
              }}
              className="text-text-on-dark hover:text-white transition-colors pr-2 py-2"
            >
              <svg className={`w-4 h-4 transition-transform ${teamsOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
          {teamsOpen && (
            <div
              className="absolute top-full left-0 pt-1 z-50"
              onMouseEnter={cancelClose}
              onMouseLeave={handleMouseLeave}
            >
              <div className="bg-white rounded-lg shadow-elevated border border-stone/20 py-1 min-w-[220px]">
                {user_teams.length === 0 ? (
                  <div className="px-4 py-3 text-base text-text-muted">No teams yet</div>
                ) : (
                  user_teams.map(team => (
                    <Link
                      key={team.id}
                      href={`/teams/${team.id}`}
                      onClick={() => setTeamsOpen(false)}
                      className={`block px-4 py-2 text-base transition-colors ${
                        current_team?.id === team.id
                          ? 'bg-charcoal/10 text-text-primary font-medium'
                          : 'text-text-primary hover:bg-stone/30'
                      }`}
                    >
                      <span>{team.name}</span>
                      <span className="text-text-muted ml-2 text-sm">{team.member_count} members</span>
                    </Link>
                  ))
                )}
                <div className="border-t border-stone/20 mt-1 pt-1">
                  <Link
                    href="/teams"
                    onClick={() => setTeamsOpen(false)}
                    className="block px-4 py-2 text-base text-text-secondary hover:bg-stone/30 transition-colors"
                  >
                    View All Teams
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        <PeopleNav charts={published_charts || []} canManage={!!can_manage_charts} />
        <Link href="/company" className="font-sans text-base font-medium text-text-on-dark hover:text-white transition-colors px-3 py-2">
          Company
        </Link>
        <Link href="/projects" className="font-sans text-base font-medium text-text-on-dark hover:text-white transition-colors px-3 py-2">
          Projects
        </Link>
      </div>

      <div className="flex items-center gap-3">
        {canSeeCompanyBilling && (
          <Link
            href="/admin/billing"
            className="text-text-on-dark hover:text-white transition-colors"
            title="Billing"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h2m2 0h4M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </Link>
        )}
        {isAdmin && (
          <Link
            href="/agenda_templates"
            className="text-text-on-dark hover:text-white transition-colors"
            title="Agenda Templates"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </Link>
        )}
        {isAdmin && (
          <Link
            href="/admin/backups"
            className="text-text-on-dark hover:text-white transition-colors"
            title="Tenant Backups"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10a2 2 0 002 2h12a2 2 0 002-2V7M4 7l8-4 8 4M4 7h16M12 11v6m-3-3h6" />
            </svg>
          </Link>
        )}
        {isAdmin && (
          <Link
            href="/admin/settings"
            className="text-text-on-dark hover:text-white transition-colors"
            title="Admin Settings"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </Link>
        )}
        {impersonation ? (
          <div title={impersonation.super_admin.full_name}>
            <UserAvatar user={current_user} size="xs" variant="primary" />
          </div>
        ) : (
          <Link href="/profile" className="hover:opacity-80 transition-opacity">
            <UserAvatar user={current_user} size="xs" variant="primary" />
          </Link>
        )}
        {!impersonation && (
          <button
            type="button"
            onClick={() => router.delete('/users/sign_out', { data: { authenticity_token: csrf_token ?? '' } })}
            className="font-sans text-sm font-medium text-text-on-dark hover:text-white transition-colors"
          >
            Sign Out
          </button>
        )}
      </div>
    </nav>
  )
}

/**
 * People menu — built like the Teams menu above: the label itself still
 * navigates, the chevron toggles, and the panel opens on hover with the same
 * short delayed close. The Results Ownership Chart lives in here rather than
 * as its own top-level entry.
 */
export function PeopleNav({ charts, canManage }: { charts: { id: number; name: string }[]; canManage: boolean }) {
  const [open, setOpen] = useState(false)
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cancelClose = () => {
    if (closeTimeout.current) { clearTimeout(closeTimeout.current); closeTimeout.current = null }
  }
  const scheduleClose = () => {
    cancelClose()
    closeTimeout.current = setTimeout(() => setOpen(false), 200)
  }

  // canManage is the legacy list-navigation permission, not edit permission.
  // List viewers (including Guides) can reach all charts even with none published.
  const showChartEntry = canManage || charts.length > 0
  const singleChartHref = charts.length === 0
    ? '/accountability_charts'
    : `/accountability_charts/${charts[0].id}`

  return (
    <div
      data-testid="people-nav"
      className="relative"
      onMouseEnter={() => { cancelClose(); setOpen(true) }}
      onMouseLeave={scheduleClose}
    >
      <div className="flex items-center">
        <Link
          href="/people"
          className="font-sans text-base font-medium text-text-on-dark hover:text-white transition-colors pl-3 pr-1 py-2"
        >
          People
        </Link>
        <button
          onClick={(e) => {
            e.stopPropagation()
            setOpen(!open)
          }}
          className="text-text-on-dark hover:text-white transition-colors pr-2 py-2"
        >
          <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      {open && (
        <div
          className="absolute top-full left-0 pt-1 z-50"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          <div className="bg-white rounded-lg shadow-elevated border border-stone/20 py-1 min-w-[220px]">
            <Link
              href="/people"
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-base text-text-primary hover:bg-stone/30 transition-colors"
            >
              People
            </Link>
            {showChartEntry && (
              <div className="border-t border-stone/20 mt-1 pt-1">
                {charts.length > 1 ? (
                  <>
                    {/* Several published charts: each one stays reachable by name. */}
                    <div className="px-4 py-1.5 text-xs font-semibold text-text-muted uppercase tracking-wider">
                      {LABELS.features}
                    </div>
                    {charts.map(chart => (
                      <Link
                        key={chart.id}
                        href={`/accountability_charts/${chart.id}`}
                        onClick={() => setOpen(false)}
                        className="block px-4 py-2 text-base text-text-primary hover:bg-stone/30 transition-colors truncate"
                      >
                        {chart.name}
                      </Link>
                    ))}
                  </>
                ) : (
                  <Link
                    href={singleChartHref}
                    data-testid="people-nav-chart-link"
                    onClick={() => setOpen(false)}
                    className="block px-4 py-2 text-base text-text-primary hover:bg-stone/30 transition-colors"
                  >
                    {LABELS.feature}
                  </Link>
                )}
                {canManage && charts.length > 0 && (
                  <Link
                    href="/accountability_charts"
                    onClick={() => setOpen(false)}
                    className="block px-4 py-2 text-base text-text-secondary hover:bg-stone/30 transition-colors"
                  >
                    View all charts
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
